import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { QuestionCircleIcon } from 'assets/icons';
import Tooltip from 'shared/components/Tooltip';
import { useAvatarGroupSelectOptions } from 'shared/components/Avatars/AvatarGroupSelect';
import { Link } from 'react-router-dom';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { getUserFullName } from 'shared/utils';
import Select from 'react-select';
import useIsMobile from 'shared/hooks/useIsMobile';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Button } from '@televet/kibble-ui/build/components/Button';

interface IOptionType {
  value: string;
  label: string;
}

interface IParticipantsInputProps {
  selectedUserIds: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  customDescription?: string;
  showAddAllTeamMembers?: boolean;
  showTooltip?: boolean;
  autofocus?: boolean;
}

const ParticipantsInput = ({
  selectedUserIds,
  customDescription,
  showAddAllTeamMembers = true,
  showTooltip = true,
  required = false,
  autofocus = false,
  onChange,
}: IParticipantsInputProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const { clinicUser } = useClinicUser();
  const groupSelectOptions = useAvatarGroupSelectOptions();
  const assigneeOptions: IOptionType[] = useMemo(
    () =>
      groupSelectOptions
        .map(({ value }) => {
          const { id, firstName, lastName = '' } = value;
          return { value: id, label: getUserFullName({ firstName, lastName }) };
        })
        .filter(({ value }) => value !== clinicUser?.id),
    [groupSelectOptions, clinicUser],
  );
  const assigneeOptionsMap = useMemo(() => {
    return assigneeOptions.reduce<{ [id: string]: IOptionType }>((options, curr) => {
      options[curr.value] = curr;
      return options;
    }, {});
  }, [assigneeOptions]);

  const value = useMemo(
    () => selectedUserIds.map((id) => ({ value: id, label: assigneeOptionsMap[id].label })),
    [selectedUserIds, assigneeOptionsMap],
  );

  const handleAssigneesChange = (newAssignees: Readonly<IOptionType[]>): void => {
    onChange(newAssignees.map(({ value }) => value));
  };

  const handleAddAllTeamMembers = (): void => {
    onChange(assigneeOptions.map(({ value }) => value));
  };

  return (
    <>
      <HelpTextContainer>
        <Label>
          <span>
            Participants
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </span>
          {showTooltip && (
            <Tooltip
              content={
                <span>
                  {
                    'Participants will be notified of new messages in this channel if they\'ve chosen to recieve notifications for conversations "assigned to me" on '
                  }
                  {isMobile ? (
                    'the My Notifications page'
                  ) : (
                    <Link to={`${RouteBasePaths.Settings}${SettingsRoutes.UserNotifications}`}>
                      {'the My Notifications page'}
                    </Link>
                  )}
                  .
                </span>
              }
              interactive={true}
              touch={true}
            >
              <StyledQuestionCircleIcon />
            </Tooltip>
          )}
        </Label>
        <Description>
          {customDescription ? (
            customDescription
          ) : (
            <>
              <p>
                Choose who to notify when new messages are sent in this channel. All other team members will still be
                able to view and contribute to the discussion.
              </p>
              <p>
                In private channels, only the participants will be notified and be able to view and contribute to the
                discussion.
              </p>
            </>
          )}
        </Description>
        {selectedUserIds.length === assigneeOptions.length || !showAddAllTeamMembers ? null : (
          <ActionButtonsContainer>
            <Button onClick={handleAddAllTeamMembers}>Add all team members</Button>
          </ActionButtonsContainer>
        )}
      </HelpTextContainer>
      <SelectContainer>
        <Select
          autoFocus={autofocus}
          options={assigneeOptions}
          placeholder="Select team members..."
          noOptionsMessage={({ inputValue }): string =>
            selectedUserIds.length === assigneeOptions.length
              ? 'No more team members to select'
              : `No team members found for "${inputValue}"`
          }
          menuPosition="fixed"
          isMulti={true}
          hideSelectedOptions={true}
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          value={value}
          onChange={(newAssignees): void => handleAssigneesChange(newAssignees)}
          onBlur={(event): void => event.preventDefault()}
          styles={{
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            control: (provided, state): any => ({
              ...provided,
              borderColor: state.isFocused ? 'var(--chakra-colors-text-brand)' : 'hsl(0,0%,82%)',
              borderRadius: '7px',
              '&:hover': {
                borderColor: 'hsl(0,0%,70%) !important',
              },
              boxShadow: 'none',
              cursor: 'pointer',
            }),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            option: (provided, state): any => ({
              ...provided,
              background: state.isSelected || state.isFocused ? '#e6f4f6' : provided.background,
              cursor: 'pointer',
              '&:hover': {
                background: '#e6f4f6',
              },
              '&:active': {
                background: '#e6f4f6',
              },
            }),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            multiValue: (provided): any => ({
              ...provided,
              background: '#eeeff1',
              color: '#060a3a',
              borderRadius: '5px',
            }),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            multiValueRemove: (provided): any => ({
              ...provided,
              '&:hover': {
                background: 'hsl(0,0%,82%)',
                color: '#060a3a',
              },
            }),
          }}
        />
      </SelectContainer>
    </>
  );
};

const RequiredIndicator = styled.span`
  color: var(--chakra-colors-text-danger);
  margin-left: 2px;
`;

const Label = styled.label`
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
`;

const HelpTextContainer = styled.div`
  display: flex;
  flex-flow: column;

  > div {
    display: flex;
    align-items: center;
  }
`;

const Description = styled.div`
  color: var(--chakra-colors-gray-800);
  display: block !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: 5px;

  p + p {
    margin-top: 5px;
  }
`;

const StyledQuestionCircleIcon = styled(QuestionCircleIcon)`
  width: 13px;
  height: auto;
  padding: 2px;
  margin-left: 3px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px 0;
`;

const SelectContainer = styled.div`
  margin-top: 10px;
`;

export default ParticipantsInput;
